import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, Building2, ClipboardType, Files, Upload, CheckCircle2, XCircle, X, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DocumentViewerDialog } from '@/components/projects/ScopeDocuments';
import api from '@/lib/api';
import { toast } from 'sonner';

const DOC_TYPES = [
  { value: 'contract_wo_loi', label: 'Contract / Work Order / LOI' },
  { value: 'boq', label: 'BOQ' },
  { value: 'scope_of_work', label: 'Scope of Work' },
  { value: 'accepted_quotation', label: 'Accepted Quotation' },
  { value: 'exclusions', label: 'Exclusions / Clarifications' },
  { value: 'tender_spec', label: 'Tender / Specification' },
  { value: 'other', label: 'Other' },
];
const STATUSES = [
  { value: 'binding', label: 'Binding' },
  { value: 'incorporated', label: 'Incorporated' },
  { value: 'supporting', label: 'Supporting Context' },
  { value: 'superseded', label: 'Superseded' },
];
const DOC_LABEL = Object.fromEntries(DOC_TYPES.map((d) => [d.value, d.label]));
const MAX_FILES = 6;
const PER_FILE = 15 * 1024 * 1024;
const TOTAL = 40 * 1024 * 1024;
const kb = (b) => (b >= 1024 * 1024 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const CreateProjectDialog = ({ onCreated, trigger }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [name, setName] = useState('');
  const [client, setClient] = useState('');
  const [noticeDays, setNoticeDays] = useState('14');
  const [tab, setTab] = useState('files');
  const [scopeText, setScopeText] = useState('');
  const [docs, setDocs] = useState([]); // {file, doc_type, status}
  const [summary, setSummary] = useState(null); // extract-scope-multi response
  const [viewDoc, setViewDoc] = useState(null); // {file_id, filename} being previewed
  const fileRef = useRef();

  const reset = () => {
    setName(''); setClient(''); setNoticeDays('14');
    setTab('files'); setScopeText(''); setDocs([]); setSummary(null);
  };

  const addFiles = (list) => {
    setSummary(null);
    const incoming = Array.from(list || []);
    let next = [...docs];
    for (const f of incoming) {
      if (next.length >= MAX_FILES) { toast.error(`Maximum ${MAX_FILES} files`); break; }
      if (!f.name.toLowerCase().endsWith('.pdf')) { toast.error(`${f.name}: only PDF files are allowed`); continue; }
      if (f.size > PER_FILE) { toast.error(`${f.name}: exceeds 15 MB`); continue; }
      if (next.some((d) => d.file.name === f.name && d.file.size === f.size)) continue;
      next.push({ file: f, doc_type: 'contract_wo_loi', status: 'binding' });
    }
    if (next.reduce((s, d) => s + d.file.size, 0) > TOTAL) { toast.error('Total upload exceeds 40 MB'); return; }
    setDocs(next);
    if (fileRef.current) fileRef.current.value = '';
  };

  const updateDoc = (i, k, v) => { setDocs((d) => d.map((x, idx) => (idx === i ? { ...x, [k]: v } : x))); setSummary(null); };
  const removeDoc = (i) => { setDocs((d) => d.filter((_, idx) => idx !== i)); setSummary(null); };

  const processFiles = async () => {
    if (docs.length === 0) { toast.error('Add at least one PDF'); return; }
    setExtracting(true);
    try {
      const fd = new FormData();
      docs.forEach((d) => { fd.append('files', d.file); fd.append('doc_types', d.doc_type); fd.append('statuses', d.status); });
      const r = await api.post('/projects/extract-scope-multi', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSummary(r.data);
      if (r.data.files_extracted_ok === 0) toast.error('No readable text found in any file');
      else toast.success(`Extracted ${r.data.files_extracted_ok} of ${r.data.files_uploaded} document(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not process files'); }
    finally { setExtracting(false); }
  };

  const submit = async () => {
    if (!name.trim()) { toast.error('Project name is required'); return; }
    let scope = ''; let scope_documents = null;
    if (tab === 'paste') {
      scope = scopeText.trim();
      if (!scope) { toast.error('Paste the scope, or switch to Upload files'); return; }
    } else {
      if (!summary) { toast.error('Click "Process files" first to extract the documents'); return; }
      if (summary.files_extracted_ok === 0) { toast.error('No readable text extracted — add native (non-scanned) PDFs'); return; }
      scope = summary.combined_text;
      scope_documents = summary.documents;
    }
    setLoading(true);
    try {
      const r = await api.post('/projects', {
        name, client, original_scope: scope, scope_documents,
        notice_period_days: parseInt(noticeDays, 10) || 14,
      });
      toast.success('Project created');
      setOpen(false); reset();
      onCreated && onCreated(r.data);
      navigate(`/projects/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create project'); }
    finally { setLoading(false); }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        {trigger || <Button className="bg-primary hover:bg-ink-700" data-testid="create-project-btn"><Plus className="h-4 w-4 mr-2" />New Project</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="create-project-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Building2 className="h-4 w-4" />Create a project</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">Name it, then give Recoup the baseline — upload the project documents or paste the scope.</p>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Project name *</Label>
              <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Downtown Lab Fitout" data-testid="project-name" autoFocus />
            </div>
            <div>
              <Label className="text-xs">Client (optional)</Label>
              <Input className="mt-1" value={client} onChange={(e) => setClient(e.target.value)} placeholder="e.g. Acme Developers" data-testid="project-client" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Project baseline *</Label>
            <Tabs value={tab} onValueChange={setTab} className="mt-1.5">
              <TabsList className="w-full">
                <TabsTrigger value="files" className="flex-1" data-testid="scope-tab-files"><Files className="h-4 w-4 mr-1.5" />Upload documents</TabsTrigger>
                <TabsTrigger value="paste" className="flex-1" data-testid="scope-tab-paste"><ClipboardType className="h-4 w-4 mr-1.5" />Paste text</TabsTrigger>
              </TabsList>

              {/* Multi-document upload */}
              <TabsContent value="files" className="mt-3 space-y-3">
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 ring-1 ring-amber-100 px-3 py-2 text-[11px] text-amber-800">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span><b>Native text PDFs only</b> — scanned/photographed PDFs aren't supported yet. Max {MAX_FILES} files, 15 MB each, 40 MB total.</span>
                </div>

                <input ref={fileRef} type="file" accept=".pdf" multiple className="hidden" data-testid="project-files-input" onChange={(e) => addFiles(e.target.files)} />
                {docs.length < MAX_FILES && (
                  <button type="button" onClick={() => fileRef.current?.click()} data-testid="add-files-btn"
                    className="w-full rounded-xl border-2 border-dashed border-slate-200 hover:border-brand/40 bg-slate-50 px-4 py-4 text-center transition-colors">
                    <Upload className="h-5 w-5 mx-auto text-slate-400" />
                    <div className="text-sm text-slate-600 mt-1.5">Click to add PDF documents ({docs.length}/{MAX_FILES})</div>
                  </button>
                )}

                {docs.map((d, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 p-2.5 space-y-2" data-testid={`doc-row-${i}`}>
                    <div className="flex items-center gap-2">
                      <Files className="h-4 w-4 text-slate-400 shrink-0" />
                      <span className="text-sm text-slate-800 truncate flex-1">{d.file.name}</span>
                      <span className="text-[11px] text-slate-400">{kb(d.file.size)}</span>
                      <button type="button" onClick={() => removeDoc(i)} className="text-slate-400 hover:text-danger-600" data-testid={`remove-doc-${i}`}><X className="h-4 w-4" /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={d.doc_type} onValueChange={(v) => updateDoc(i, 'doc_type', v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`doc-type-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value} className="text-xs">{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={d.status} onValueChange={(v) => updateDoc(i, 'status', v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`doc-status-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}

                {docs.length > 0 && (
                  <Button type="button" variant="outline" className="w-full" onClick={processFiles} disabled={extracting} data-testid="process-files-btn">
                    {extracting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}Process files &amp; preview
                  </Button>
                )}

                {/* Extraction summary */}
                {summary && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2" data-testid="extraction-summary">
                    <div className="text-xs font-semibold text-slate-700">
                      Summary — {summary.files_extracted_ok}/{summary.files_uploaded} readable · {summary.total_chars.toLocaleString()} characters (~{Math.max(1, Math.round(summary.total_chars / 1800))} pages)
                    </div>
                    {summary.files_extracted_ok < summary.files_uploaded && (
                      <div className="rounded-md border border-danger-200 bg-danger-50 p-2.5 flex gap-2" data-testid="extraction-failed-banner">
                        <AlertTriangle className="h-4 w-4 text-danger-600 shrink-0 mt-0.5" />
                        <div className="text-[11px] text-danger-700">
                          <div className="font-semibold">{summary.files_uploaded - summary.files_extracted_ok} file(s) failed and were NOT added to the baseline.</div>
                          <p className="mt-0.5">Recoup accepts only <b>text-based PDFs</b>. Word docs, images, and scanned / OCR PDFs are rejected. See the reason next to each file below: {summary.documents.filter((d) => !d.extraction_ok).map((d) => d.filename).join(', ')}.</p>
                        </div>
                      </div>
                    )}
                    {summary.documents.map((doc, i) => (
                      <div key={i} className={`flex items-center gap-2 text-[11px] ${doc.extraction_ok ? '' : 'text-danger-700 font-medium'}`} data-testid={`summary-doc-${i}`}>
                        {doc.extraction_ok ? <CheckCircle2 className="h-3.5 w-3.5 text-money-600 shrink-0" /> : <XCircle className="h-3.5 w-3.5 text-danger-600 shrink-0" />}
                        {doc.file_id ? (
                          <button type="button" onClick={() => setViewDoc({ file_id: doc.file_id, filename: doc.filename })}
                            className="truncate text-brand hover:underline" data-testid={`summary-doc-view-${i}`}>{doc.filename}</button>
                        ) : (
                          <span className="truncate">{doc.filename}</span>
                        )}
                        <span className="text-slate-400">· {DOC_LABEL[doc.doc_type] || doc.doc_type}</span>
                        <span className="ml-auto text-slate-400">{doc.extraction_ok ? `${doc.char_count.toLocaleString()} chars` : (doc.note || 'unreadable — not processed')}</span>
                      </div>
                    ))}
                    {summary.warnings?.map((w, i) => (
                      <div key={i} className="flex items-start gap-1.5 text-[11px] text-amber-700"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />{w}</div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Paste text (unchanged) */}
              <TabsContent value="paste" className="mt-3">
                <Textarea rows={6} value={scopeText} onChange={(e) => setScopeText(e.target.value)} data-testid="project-scope"
                  placeholder="Paste the work order, BOQ or contract scope. Spell out inclusions and — crucially — exclusions." />
                <p className="text-[11px] text-slate-400 mt-1.5">The sharper the scope (especially exclusions), the more opportunities Recoup spots.</p>
              </TabsContent>
            </Tabs>
          </div>

          <div className="rounded-lg bg-brand-50/60 ring-1 ring-brand-100 p-3">
            <Label className="text-xs font-semibold text-slate-700">Notice period</Label>
            <div className="flex items-center gap-2 mt-1.5">
              <Input className="w-24" type="number" min="1" value={noticeDays} onChange={(e) => setNoticeDays(e.target.value)} data-testid="project-notice-period" />
              <span className="text-sm text-slate-500">days to give notice after an event</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={submit} disabled={loading} className="bg-money hover:bg-money-700" data-testid="project-create-submit">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />} Create project
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <DocumentViewerDialog fileId={viewDoc?.file_id} filename={viewDoc?.filename}
      open={!!viewDoc} onOpenChange={(o) => { if (!o) setViewDoc(null); }} />
    </>
  );
};
